































































































































































































































import {
  Component,
  Prop,
  Vue,
  Watch
} from "@/shared/vuepack";
import Button from "@/shared/components/ui/Button.vue";
import Input from "@/shared/components/ui/Input.vue";
import {
  InvestmentCustodian,
  UserPermission,
} from "@/shared/types";
import {
  Environment,
  getEnvironment
} from "@/shared/env";
import { BankCustodian, EmployerPartner } from "@/admin/types";
import { Validator } from "vee-validate";
import ErrorSummary from "@/shared/components/ErrorSummary.vue";
import FileUploadSettings from "./FileUploadSettings.vue";
import CensusManagerView from "./CensusManagerView.vue";
import { useNotifierStore } from "@/shared/store/notifier";
import { useFeatureFlagStore } from "@/shared/store/featureFlag";
import { useRolePermissionsStore } from "@/shared/store/rolePermissions";
import { useInstrumentListStore } from "@/admin/store/instrumentList";
import { useEmployerStore } from "@/admin/store/employer";
import GenericLoadingSpinner from "@/shared/components/ui/GenericLoadingSpinner.vue";

@Component({
  components: {
    Input,
    Button,
    ErrorSummary,
    FileUploadSettings,
    CensusManagerView,
    GenericLoadingSpinner,
  }
})
export default class EmployerSettings extends Vue {
  $validator: Validator;

  @Prop() employerId: number;
  @Prop() displayTdaSettings: boolean;
  @Prop() displayAvidiaOptions: boolean;

  notifierStore = useNotifierStore();
  employerStore = useEmployerStore();
  instrumentListStore = useInstrumentListStore();
  featureFlagStore = useFeatureFlagStore();
  rolePermissionsStore = useRolePermissionsStore();

  isSubmitting = false;
  thresholdAccountCreate = null;
  thresholdMoneyTransfer = null;
  investmentProvider = InvestmentCustodian.None;
  hasAdvisory: boolean = null;
  hasRiaManaged: boolean = null;
  hasSelfDirected: boolean = null;
  isCuratedRtqEnabled: boolean = null;
  partnerHasAdvisory = false;
  partnerHasRiaManaged = false;
  partnerHasSelfDirected = false;
  allowPreEnrollment = false;
  partnerAllowsPreEnrollment = false;
  pendingEnrollmentCount = 0;
  instrumentListId: number = null;
  partnerListName: string;
  ssoUserSyncStatus = false;
  partner: EmployerPartner = { id: 0 };
  isSubmittingInvestmentThreshold = false;
  isSweepThresholdEnabled = false;
  sweepThreshold = null;
  isSubmittingSweepThreshold = false;
  enableRetailContributionWarning = false;

  mounted () {
    this.employerStore.loadRetailContributionWarning(this.employerId);
    this.employerStore.loadEmployerFundingDays(this.employerId);
    this.employerStore.loadEmployerInvestmentType(this.employerId);
    this.mapInvestmentTypes(this.isInvestmentEnrollmentTypeLoaded);
    this.employerStore.loadEmployer(this.employerId)
      .then(employer => {
        this.ssoUserSyncStatus = employer.ssoUserSyncStatus;
        if (employer.partner) {
          this.partner = employer.partner;
        } else {
          this.partner = { id: 0 };
        }
      });
  }

  get isInvestmentEnrollmentTypeLoaded (): boolean {
    return this.employerStore.isEmployerInvestmentEnrollmentTypeLoaded(this.employerId);
  }

  get hasEditPermission (): boolean {
    return this.featureFlagStore.flags.hsa4475allowRolePermissions
      ? this.rolePermissionsStore.userHasAdminPermission(UserPermission.AdminPortalEmployerEdit)
      : true;
  }

  get showSsoUserSync () {
    return this.partner.custodian === BankCustodian.HsaBank;
  }

  get ssoUserSyncDisabled () {
    return this.partner.ssoUserSyncStatus === true;
  }

  get ssoUserSyncDisabledMessage () {
    if (this.ssoUserSyncDisabled) {
      return "This option cannot be changed because SSO is already turned on at the partner level.";
    }

    return "";
  }

  get investmentEnrollmentType () {
    return this.employerStore.employerInvestmentEnrollmentType(this.employerId);
  }

  @Watch("isInvestmentEnrollmentTypeLoaded")
  mapInvestmentTypes (isLoaded) {
    if (isLoaded) {
      this.hasAdvisory = this.investmentEnrollmentType.hasAdvisory;
      this.partnerHasAdvisory = this.investmentEnrollmentType.partnerHasAdvisory;
      this.hasRiaManaged = this.investmentEnrollmentType.hasRiaManaged;
      this.partnerHasRiaManaged = this.investmentEnrollmentType.partnerHasRiaManaged;
      this.hasSelfDirected = this.investmentEnrollmentType.hasSelfDirected;
      this.partnerHasSelfDirected = this.investmentEnrollmentType.partnerHasSelfDirected;
      this.allowPreEnrollment = this.investmentEnrollmentType.allowPreEnrollment;
      this.partnerAllowsPreEnrollment = this.investmentEnrollmentType.partnerAllowsPreEnrollment;
      this.pendingEnrollmentCount = this.investmentEnrollmentType.pendingEnrollmentCount;
      this.isCuratedRtqEnabled = this.investmentEnrollmentType.isCuratedRtqEnabled;
    }
  }

  @Watch("retailEmployerForCurrentEmployer", { immediate: true })
  retailEmployerLoaded () {
    this.enableRetailContributionWarning = this.employerStore.retailContributionWarning[this.employerId];
  }

  get retailEmployerForCurrentEmployer (): boolean | undefined {
    return this.employerStore.retailContributionWarning[this.employerId];
  }

  created () {
    if (this.displayTdaSettings) {
      this.employerStore.loadEmployerTdaPlanId(this.employerId);
    }
    this.mapInvestmentThreshold();
  }

  save () {
    if (this.isSubmitting) {
      return;
    }
    this.isSubmitting = true;
    if (this.employerStore.fundingDays[this.employerId] !== undefined) {
      this.employerStore.updateEmployerFundingDays(
        { employerId: this.employerId, days: this.employerStore.fundingDays[this.employerId] })
        .then(() => {
          this.notifierStore.setSuccess("Funding Hold Days have been saved");
        })
        .catch(this.handleError);
    }
    if (this.employerStore.tdaPlanId[this.employerId] !== undefined && this.displayTdaSettings) {
      this.employerStore.updateEmployerTdaPlanId(
        { employerId: this.employerId, tdaPlanId: this.employerStore.tdaPlanId[this.employerId] })
        .then(() => {
          this.notifierStore.setSuccess("The TDA Plan ID has been saved");
        })
        .catch(this.handleError);
    }
    this.employerStore.updateRetailContributionWarning(
      { employerId: this.employerId, isEnabled: this.enableRetailContributionWarning })
      .then(() => {
        this.notifierStore.setSuccess("Enable Retail Contributions Warning setting has been saved");
      })
      .catch(this.handleError);
    this.isSubmitting = false;
  }

  saveInvestmentType (): void {
    if (this.isSubmitting) return;
    this.isSubmitting = true;
    this.employerStore.setEmployerInvestmentType({
      employerId: this.employerId, investmentType: {
        hasAdvisory: this.nullOrBool(this.hasAdvisory),
        hasRiaManaged: this.nullOrBool(this.hasRiaManaged),
        hasSelfDirected: this.nullOrBool(this.hasSelfDirected),
        allowPreEnrollment: this.nullOrBool(this.allowPreEnrollment),
        isCuratedRtqEnabled: this.nullOrBool(this.isCuratedRtqEnabled)
      }
    })
      .then(() => {
        this.notifierStore.setSuccess("Investment Enrollment settings have been saved");
      })
      .finally(() => {
        this.investmentEnrollmentType.hasAdvisory = this.hasAdvisory;
        this.investmentEnrollmentType.hasRiaManaged = this.hasRiaManaged;
        this.investmentEnrollmentType.hasSelfDirected = this.hasSelfDirected;
        this.investmentEnrollmentType.allowPreEnrollment = this.allowPreEnrollment;
        this.isSubmitting = false;
      });
  }

  saveInvestmentThreshold () {
    this.$validator.validateAll("investmentThreshold").then(isValid => {
      if (isValid && this.employerStore.investmentThreshold[this.employerId] !== undefined) {
        this.isSubmittingInvestmentThreshold = true;
        this.employerStore.updateEmployerInvestmentThreshold({
          employerId: this.employerId,
          investmentThreshold:
          {
            investmentProvider: this.investmentProvider,
            accountCreate: this.thresholdAccountCreate,
            moneyTransfer: this.thresholdMoneyTransfer,
            sweepThreshold: 0,
            isSweepThresholdEnabled: false
          }
        })
          .then(() => {
            this.notifierStore.setSuccess("Investment Threshold have been saved");
          })
          .catch(this.handleError)
          .finally(() => this.isSubmittingInvestmentThreshold = false);
      }
    });
  }

  saveSweepThreshold () {
    this.$validator.validateAll("investmentThreshold").then(isValid => {
      if (isValid && this.employerStore.investmentThreshold[this.employerId] !== undefined) {
        this.isSubmittingSweepThreshold = true;
        this.employerStore.updateEmployerSweepThreshold({
          employerId: this.employerId,
          investmentThreshold:
              {
                investmentProvider: this.investmentProvider,
                accountCreate: null,
                moneyTransfer: null,
                sweepThreshold: this.sweepThreshold,
                isSweepThresholdEnabled: this.isSweepThresholdEnabled
              }
        })
            .then(() => {
              this.notifierStore.setSuccess("Auto Invest Threshold have been saved");
            })
            .catch(this.handleError)
            .finally(() => this.isSubmittingSweepThreshold = false);
      }
    });
  }

  saveSsoUserSyncStatus () {
    if (this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;

    this.employerStore.updateSsoUserSyncStatus({
      employerId: this.employerId,
      ssoUserSyncStatus: this.ssoUserSyncStatus
    }).finally(() => {
      this.isSubmitting = false;
    });
  }

  mapInvestmentThreshold () {
    this.employerStore.loadEmployerInvestmentThreshold(this.employerId).then(() => {
      this.investmentProvider = this.employerStore.investmentThreshold[this.employerId].investmentProvider;
      this.thresholdAccountCreate = this.employerStore.investmentThreshold[this.employerId].accountCreate;
      this.thresholdMoneyTransfer = this.employerStore.investmentThreshold[this.employerId].moneyTransfer;
      this.sweepThreshold = this.employerStore.investmentThreshold[this.employerId].sweepThreshold;
      this.isSweepThresholdEnabled = this.employerStore.investmentThreshold[this.employerId].isSweepThresholdEnabled;
    });
  }

  IsProd () {
    return getEnvironment() === Environment.Prod;
  }

  handleError (err: any): void {
    this.notifierStore.setDanger((err && err.errorMessage)
      || "Error processing your request. Please try again later"
    );
  }

  get investmentThresholdSubmitDisabled () {
    return this.isSubmittingInvestmentThreshold || this.$validator.errors.any("investmentThreshold");
  }

  get sweepThresholdSubmitDisabled () {
    return this.isSubmittingSweepThreshold || this.$validator.errors.any("sweepThreshold");
  }

  nullOrBool (input?) {
    if (input === "null") return null;
    return input;
  }

  get displayInvestmentThresholdSelection () {
    return this.investmentProvider === InvestmentCustodian.Tda
      || this.investmentProvider === InvestmentCustodian.Drivewealth;
  }

  get displayInvestmentTypeSelection () {
    return this.investmentProvider === InvestmentCustodian.Drivewealth;
  }

  partnerAllowed (isAllowed: boolean): string {
    return isAllowed ? "Allowed" : "Not Allowed";
  }

  get investmentCustodianSelectOptions () {
    return Object.keys(InvestmentCustodian)
      .map(k => ({ value: InvestmentCustodian[k], text: k.toUpperCase() }));
  }

  setInvestmentProvider () {
    if (!this.displayInvestmentThresholdSelection) {
      // reset threshold values
      this.$nextTick(() => {
        this.thresholdAccountCreate = null;
        this.thresholdMoneyTransfer = null;
      });
    }
  }
}
